import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ChartSplitPiece, splitAtChartBlocks } from '@shared/chart-split';
import {
  mayHoldOutcome,
  OUTCOME_MARKER_NAME,
  OutcomeEnvelope,
  splitAtOutcomes,
  streamingVisibleText,
} from '@shared/outcome';
import { MarkdownComponent } from 'ngx-markdown';
import { ResponseChartComponent } from '../../../charts/response-chart.component';
import { makeLinksInert } from '../../../shared/markdown';
import { OutcomeBlockComponent } from '../messages-listing/message/outcome/outcome-block.component';

/** A word and the whitespace after it — the artifact's streaming unit. */
const TOKEN = /\S+\s*/g;

/**
 * A chart block as core-ai streams it, in one piece where its marker stood
 * (`chart_placement.ChartStreamFilter`): its JSON is a single line. Revealed
 * whole, so the screen never shows half a chart's source.
 */
const CHART_BLOCK = /```chart\n[^\n]*\n```/g;

/**
 * SK-135: an outcome marker line (`@shared/outcome`), revealed in one step
 * like a chart block, so the reveal never stops inside one.
 */
const OUTCOME_MARKER_LINE = new RegExp(
  `^<!-- /?${OUTCOME_MARKER_NAME}[^\\n]*-->$`,
  'gm',
);

/** A piece of the live text: prose, a chart, or an outcome (SK-135). */
type LivePiece =
  | ChartSplitPiece
  | {
      readonly kind: 'outcome';
      readonly envelope: OutcomeEnvelope;
      readonly body: string;
      readonly open: boolean;
    };

/** Past this many words behind, the cadence speeds up proportionally. */
const CATCH_UP_WORDS = 40;

/**
 * At most one reveal step per frame: a far-behind burst plays words faster
 * than that, and each step re-renders the whole markdown — the due words of a
 * frame land together instead.
 */
const MIN_STEP_MS = 16;

/** The index of the first word ending after `offset` (`ends` is ascending). */
const firstWordAfter = (ends: readonly number[], offset: number): number => {
  let low = 0;
  let high = ends.length;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (ends[mid] <= offset) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
};

/**
 * The delay before the NEXT word, per the SB-3651 simulation's
 * `streamAnswer()`: ~38ms a word (±60%), a beat after a sentence (+180) or a
 * comma (+60), an occasional hesitation (6% +220), a longer one between
 * paragraphs (+420). `backlog` is how many received words are still hidden:
 * a burst far ahead of the reveal (Azure's sync content filter delivers in
 * bursts) plays faster instead of lagging seconds behind. `random` is
 * injectable so the cadence is testable.
 */
export const nextWordDelayMs = (
  word: string,
  backlog: number,
  random: () => number = Math.random,
): number => {
  let delay = 38 * (1 + (random() * 2 - 1) * 0.6);
  const core = word.trimEnd();
  const trailing = word.slice(core.length);
  if (trailing.split('\n').length > 2) {
    // A blank line after the word: a new paragraph.
    delay += 420;
  } else if ('.:;!?»)'.includes(core.at(-1) ?? '')) {
    delay += 180;
  } else if (core.endsWith(',')) {
    delay += 60;
  }
  if (random() < 0.06) {
    delay += 220;
  }
  return delay / Math.max(1, backlog / CATCH_UP_WORDS);
};

/**
 * SB-3651 — the live-streamed answer, revealed word by word at the
 * simulation's cadence instead of jumping to whatever the socket just
 * delivered. Presentation only — the store's `text` (passed in as
 * {@link text}) stays authoritative and is never written back to.
 * {@link caughtUp} tells the live turn when everything received so far is on
 * screen — and how much that is — which is how it knows a finished answer is
 * done revealing.
 */
@Component({
  selector: 'squadbox-streamed-text',
  imports: [
    MarkdownComponent,
    OutcomeBlockComponent,
    ResponseChartComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // No view encapsulation: ngx-markdown writes innerHTML, which carries no
  // _ngcontent attributes; the element selector keeps it scoped to this component.
  encapsulation: ViewEncapsulation.None,
  // A text with a chart is laid out as the saved reply lays it out (its
  // segments, gap-2 apart), so the chart sits where the handoff leaves it.
  template: `
    @if (pieces(); as pieces) {
      <div class="flex flex-col gap-2">
        @for (piece of pieces; track $index) {
          @if (piece.kind === 'chart') {
            @defer (on immediate) {
              <squadbox-response-chart
                class="wrap-normal tablet-landscape:my-2"
                [source]="piece.source"
              />
            } @placeholder {
              <div class="min-h-20"></div>
            } @error {
              <p role="status" class="typo-p2">
                {{ 'CHAT.CHART.UNAVAILABLE' | translate }}
              </p>
            }
          } @else if (piece.kind === 'outcome') {
            <!-- SK-135: the same block as the saved reply; open while its end
                 marker has not arrived. -->
            <squadbox-outcome-block
              class="wrap-normal tablet-landscape:my-2"
              [envelope]="piece.envelope"
              [body]="piece.body"
              [open]="piece.open"
              [readOnly]="true"
              [inertLinks]="inertLinks()"
            />
          } @else {
            <markdown [data]="piece.raw" (ready)="rendered()" />
          }
        }
      </div>
    } @else {
      <markdown [data]="visibleText()" (ready)="rendered()" />
    }
  `,
  // The saved reply renders each block as its own segment, 8px apart
  // (message-listing-message's gap-2): the live text matches it so nothing
  // moves when the saved reply takes over.
  styles: `
    squadbox-streamed-text markdown > * + * {
      margin-top: 8px;
    }
  `,
})
export class StreamedTextComponent {
  /** The authoritative text so far (`ChatStreamState.text`). */
  public readonly text = input.required<string>();
  /** False once the turn is done — snaps to the full text immediately. */
  public readonly live = input.required<boolean>();
  /**
   * SB-3978 B5-T21: no link in the rendered text navigates (a live Teammate
   * turn). Applied on every render, in the same task ngx-markdown writes its
   * HTML, so a link is never clickable for a frame.
   */
  public readonly inertLinks = input(false);
  /**
   * The text's length whenever all of it is displayed, -1 while the reveal is
   * behind. A length, not a flag: a caught-up report stays tied to the text it
   * was about, so one for a text that has since grown can't end the turn.
   */
  public readonly caughtUp = output<number>();

  private readonly displayedLength = signal(0);
  /**
   * A revealed prefix can end inside a delta, and inside an inline code span
   * whose closing backtick is still hidden (B2-T32). Two layers keep such a
   * prefix harmless: `inertLinks` (`makeLinksInert`) after every render of a
   * live Teammate turn, and the app-wide `SANITIZE` step
   * (`connectorSafeMarkdown`), which drops scripts and event handlers, replaces
   * media with its alt text and removes auto-loading attributes on every render.
   * Any change to either layer, or to a reader (a selector or render step that
   * feeds the markdown) or a step before one, re-runs SB-3978 T34's fixture and fuzz (core-ai#538), with no prefix skip.
   */
  protected readonly displayedText = computed(() =>
    this.text().slice(0, this.displayedLength()),
  );
  /**
   * SK-135: the revealed text minus a last line that may still become an
   * outcome marker, so marker text never reaches the screen.
   */
  protected readonly visibleText = computed(() =>
    this.live()
      ? streamingVisibleText(this.displayedText())
      : this.displayedText(),
  );
  /**
   * The shown text split at its outcomes and charts, or null when it has
   * neither — the plain markdown path, unchanged. Charts use the same splitter
   * as whole-message copy, so a table stays in its prose here as it always
   * has. While live, an outcome whose end marker has not arrived yet shows as
   * an open block that fills as the text streams in (SK-135).
   */
  protected readonly pieces = computed((): LivePiece[] | null => {
    const shown = this.visibleText();
    const hasOutcome = mayHoldOutcome(shown);
    if (!hasOutcome && !shown.includes('```chart')) {
      return null;
    }
    const outcomePieces = hasOutcome
      ? splitAtOutcomes(shown, { streaming: this.live() })
      : [{ kind: 'prose' as const, raw: shown }];
    const pieces = outcomePieces.flatMap((piece): LivePiece[] =>
      piece.kind === 'outcome'
        ? [
            {
              kind: 'outcome',
              envelope: piece.envelope,
              body: piece.body,
              open: piece.open === true,
            },
          ]
        : splitAtChartBlocks(piece.raw),
    );
    return pieces.some((piece) => piece.kind !== 'prose') ? pieces : null;
  });
  /**
   * Where each word of the text ends, scanned once per text change — not once
   * per revealed word, which made a long answer's reveal quadratic. No word
   * ends inside a chart block: it is revealed in one step.
   */
  private readonly wordEnds = computed(() => {
    const text = this.text();
    const ends = Array.from(text.matchAll(TOKEN), (m) => m.index + m[0].length);
    const blocks = [
      ...text.matchAll(CHART_BLOCK),
      ...text.matchAll(OUTCOME_MARKER_LINE),
    ].map((m) => [m.index, m.index + m[0].length]);
    return blocks.length === 0
      ? ends
      : ends.filter(
          (end) => !blocks.some(([start, stop]) => end > start && end < stop),
        );
  });

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Each `<markdown>` has just written its HTML. */
  protected rendered(): void {
    if (this.inertLinks()) {
      makeLinksInert(this.host.nativeElement);
    }
  }

  constructor() {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    let reported: number | null = null;
    const report = (caughtUpTo: number) => {
      if (caughtUpTo !== reported) {
        reported = caughtUpTo;
        this.caughtUp.emit(caughtUpTo);
      }
    };
    // When the next word is due. The cadence runs on this clock, not on timer
    // counts: a timer that fires late (a background tab is throttled to about
    // once a second) shows every word already due instead of crawling.
    let dueAt: number | null = null;
    const tick = signal(0);

    effect((onCleanup) => {
      tick();
      const text = this.text();
      const shown = this.displayedLength();

      // Reduced motion, or the turn is already done (the persisted message
      // replaces this element next render anyway): show everything now.
      if (prefersReducedMotion || !this.live()) {
        this.displayedLength.set(text.length);
        report(text.length);
        return;
      }

      // A reset can't display characters that no longer exist — snap down.
      if (shown > text.length) {
        this.displayedLength.set(text.length);
        return;
      }
      if (shown === text.length) {
        dueAt = null;
        report(text.length);
        return;
      }
      report(-1);

      const now = Date.now();
      dueAt ??= now;
      const ends = this.wordEnds();
      let index = firstWordAfter(ends, shown);
      let end = shown;
      while (end < text.length && dueAt <= now) {
        const next = index < ends.length ? ends[index] : text.length;
        const word = text.slice(end, next);
        end = next;
        index += 1;
        // The pause belongs AFTER the word (a beat after a full stop); the
        // backlog is the words still hidden behind it.
        dueAt += nextWordDelayMs(word, Math.max(0, ends.length - index));
      }
      if (end !== shown) {
        this.displayedLength.set(end);
        return;
      }
      const timeoutId = setTimeout(
        () => tick.update((value) => value + 1),
        Math.max(MIN_STEP_MS, dueAt - now),
      );
      onCleanup(() => clearTimeout(timeoutId));
    });
  }
}
